import React, { useState, useMemo } from 'react';
import { Button, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiFilter, FiX, FiChevronDown, FiChevronUp, FiSearch, FiRefreshCw } from 'react-icons/fi';
// ── Shared sub-components ──────────────────────────────────────────────────

const FilterSection = ({ title, count, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
      <Button onClick={() => setOpen(o => !o)} variant="ghost">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{title}</span>
          {count > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold">
              {count}
            </span>
          )}
        </div>
        {open
          ? <FiChevronUp size={13} className="tw-text-gray-400" />
          : <FiChevronDown size={13} className="tw-text-gray-400" />}
      </Button>
      {open && (
        <div className="tw-px-3 tw-py-2.5 tw-space-y-1 tw-max-h-56 tw-overflow-y-auto tw-bg-white">
          {children}
        </div>
      )}
    </div>
  );
};
FilterSection.propTypes = {
  title: PropTypes.node,
  count: PropTypes.number,
  children: PropTypes.node,
};

const CheckItem = ({ label, checked, onChange }) => (
  <label className="tw-flex tw-items-center tw-gap-2.5 tw-py-1.5 tw-px-2 tw-rounded hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="tw-w-4 tw-h-4 tw-rounded-sm tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer focus:tw-ring-2 focus:tw-ring-blue-500"
    />
    <span className="tw-text-sm tw-text-gray-700 tw-select-none tw-flex-1 tw-leading-snug">{label}</span>
  </label>
);
CheckItem.propTypes = {
  label: PropTypes.node,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
};

const SearchInput = ({ value, onChange, placeholder }) => (
  <div className="tw-relative tw-mb-2">
    <FiSearch className="tw-absolute tw-left-2.5 tw-top-2.5 tw-text-gray-400" size={13} />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="tw-absolute tw-right-2 tw-top-2 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-p-0 tw-leading-none tw-cursor-pointer"
      >
        <FiX size={11} />
      </button>
    )}
  </div>
);
SearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

// Text filter for free-text search (Q/J number)
const TextFilterSection = ({ title, value, onChange, placeholder, active }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
      <Button variant="ghost" onClick={() => setOpen(o => !o)}>
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{title}</span>
          {active && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold">1</span>
          )}
        </div>
        {open
          ? <FiChevronUp size={13} className="tw-text-gray-400" />
          : <FiChevronDown size={13} className="tw-text-gray-400" />}
      </Button>
      {open && (
        <div className="tw-px-3 tw-py-2.5 tw-bg-white">
          <div className="tw-relative">
            <FiSearch className="tw-absolute tw-left-2.5 tw-top-2.5 tw-text-gray-400" size={13} />
            <input
              type="text"
              placeholder={placeholder}
              value={value}
              onChange={e => onChange(e.target.value)}
              className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            />
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="tw-absolute tw-right-2 tw-top-2 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-p-0 tw-leading-none tw-cursor-pointer"
              >
                <FiX size={11} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
TextFilterSection.propTypes = {
  title: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  active: PropTypes.bool,
};

// ── Hierarchical Sales Unit filter (groups by region, same as CO dashboard) ──

const buildGroups = (units, search) => {
  const q = search.toLowerCase();
  const groups = {};

  units.forEach(unit => {
    const groupKey = unit.region || unit.name;
    const label    = [unit.name, unit.description].filter(Boolean).join(' - ');
    if (!groups[groupKey]) groups[groupKey] = [];
    if (!q || label.toLowerCase().includes(q) || groupKey.toLowerCase().includes(q)) {
      groups[groupKey].push(unit);
    }
  });

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, items]) => ({
      region,
      units: items.sort((a, b) => {
        if (a.name === region) return -1;
        if (b.name === region) return 1;
        return a.name.localeCompare(b.name);
      }),
    }));
};

const SalesUnitFilter = ({ units, selected, search, onSearch, onToggle, onToggleGroup, count }) => {
  const groups = useMemo(() => buildGroups(units, search), [units, search]);

  return (
    <FilterSection title="Sales Unit" count={count}>
      {units.length > 0 ? (
        <>
          <SearchInput value={search} onChange={onSearch} placeholder="Search sales unit…" />
          {groups.map(({ region, units: groupUnits }) => {
            const ids   = groupUnits.map(u => u.id);
            const allOn = ids.every(id => selected.includes(id));
            const someOn = !allOn && ids.some(id => selected.includes(id));

            return (
              <div key={region} className="tw-mb-1">
                {/* Region header — acts as select-all for the group */}
                <label className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1 tw-rounded-md tw-bg-gray-50 tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors">
                  <input
                    type="checkbox"
                    checked={allOn}
                    ref={el => { if (el) el.indeterminate = someOn; }}
                    onChange={() => onToggleGroup(ids)}
                    className="tw-w-4 tw-h-4 tw-rounded-sm tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer focus:tw-ring-2 focus:tw-ring-blue-500"
                  />
                  <span className="tw-text-xs tw-font-bold tw-text-blue-700 tw-uppercase tw-tracking-wide tw-select-none tw-flex-1">
                    {region}
                  </span>
                  <span className="tw-text-xs tw-text-gray-400">{ids.length}</span>
                </label>

                {/* Child units */}
                <div className="tw-pl-4 tw-mt-0.5 tw-space-y-0.5">
                  {groupUnits.map(unit => (
                    <CheckItem
                      key={unit.id}
                      label={[unit.name, unit.description].filter(Boolean).join(' - ')}
                      checked={selected.includes(unit.id)}
                      onChange={() => onToggle(unit.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          {groups.length === 0 && (
            <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>
          )}
        </>
      ) : (
        <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No sales units available</p>
      )}
    </FilterSection>
  );
};
SalesUnitFilter.propTypes = {
  units: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    description: PropTypes.string,
    region: PropTypes.string,
  })).isRequired,
  selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  search: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  count: PropTypes.number,
};

// ── Main sidebar ────────────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  sales_units:     [],
  sales_incharges: [],
  customers:       [],
  statuses:        [],
  q_j_number:      ''
};

const SalesFilterSidebar = ({ filterOptions, loading, filters, onApply, onReset, containerHeight }) => {
  const [local, setLocal]           = useState({ ...EMPTY_FILTERS, ...filters });
  const [unitSearch, setUnitSearch] = useState('');
  const [inchargeSearch, setInchargeSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');

  const toggle = (key, value) => {
    setLocal(prev => {
      const arr = prev[key] || [];
      return {
        ...prev,
        [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
      };
    });
  };

  const toggleGroup = (ids) => {
    setLocal(prev => {
      const current = prev.sales_units || [];
      const allOn   = ids.every(id => current.includes(id));
      return {
        ...prev,
        sales_units: allOn
          ? current.filter(id => !ids.includes(id))
          : [...new Set([...current, ...ids])]
      };
    });
  };

  const handleReset = () => {
    setLocal(EMPTY_FILTERS);
    setUnitSearch('');
    setInchargeSearch('');
    setCustomerSearch('');
    onReset();
  };

  const activeCount =
    (local.sales_units?.length     || 0) +
    (local.sales_incharges?.length || 0) +
    (local.customers?.length       || 0) +
    (local.statuses?.length        || 0) +
    (local.q_j_number?.trim()      ? 1 : 0);

  const opts         = filterOptions || {};
  const salesUnits   = opts.sales_units     || [];
  const incharges    = useMemo(() => opts.sales_incharges || [], [opts.sales_incharges]);
  const customers    = useMemo(() => opts.customers || [], [opts.customers]);
  const statuses     = opts.statuses         || [];

  const filteredIncharges = useMemo(
    () => incharges.filter(n => n.toLowerCase().includes(inchargeSearch.toLowerCase())),
    [incharges, inchargeSearch]
  );

  const filteredCustomers = useMemo(
    () => customers.filter(c => (c.name || '').toLowerCase().includes(customerSearch.toLowerCase())),
    [customers, customerSearch]
  );

  return (
    <div
      className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden tw-flex tw-flex-col"
      style={{ height: containerHeight }}
    >
      {/* Header */}
      <div className="tw-px-4 tw-py-3.5 tw-bg-gradient-to-r tw-from-blue-600 tw-to-primary-700 tw-text-white tw-flex tw-items-center tw-justify-between tw-flex-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiFilter size={15} />
          <span className="tw-font-semibold tw-text-sm">Filters</span>
          {activeCount > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-white tw-text-blue-700 tw-text-xs tw-font-bold">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={handleReset}
            className="tw-text-xs tw-text-blue-100 hover:tw-text-white tw-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0 tw-leading-none"
          >
            <FiRefreshCw size={11} /> Reset
          </button>
        )}
      </div>

      {/* Scrollable filter sections */}
      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-8 tw-flex-1">
          <LoadingSpinner size="sm" />
        </div>
      ) : (
        <div className="tw-p-3 tw-space-y-2 tw-flex-1 tw-overflow-y-auto">
          {/* Q / J Number search */}
          <TextFilterSection
            title="Quote / Job Number"
            value={local.q_j_number || ''}
            onChange={val => setLocal(prev => ({ ...prev, q_j_number: val }))}
            placeholder="e.g. Q12345, J6789, P0001…"
            active={!!(local.q_j_number?.trim())}
          />

          {/* Sales Units — hierarchical with region grouping */}
          <SalesUnitFilter
            units={salesUnits}
            selected={local.sales_units || []}
            search={unitSearch}
            onSearch={setUnitSearch}
            onToggle={(id) => toggle('sales_units', id)}
            onToggleGroup={toggleGroup}
            count={local.sales_units?.length || 0}
          />

          {/* Sales Incharge */}
          <FilterSection title="Sales Incharge" count={local.sales_incharges?.length || 0}>
            {incharges.length > 0 ? (
              <>
                <SearchInput value={inchargeSearch} onChange={setInchargeSearch} placeholder="Search incharge…" />
                {filteredIncharges.map(name => (
                  <CheckItem
                    key={name}
                    label={name}
                    checked={(local.sales_incharges || []).includes(name)}
                    onChange={() => toggle('sales_incharges', name)}
                  />
                ))}
                {filteredIncharges.length === 0 && (
                  <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>
                )}
              </>
            ) : (
              <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No data available</p>
            )}
          </FilterSection>

          {/* Customer */}
          <FilterSection title="Customer" count={local.customers?.length || 0}>
            {customers.length > 0 ? (
              <>
                <SearchInput value={customerSearch} onChange={setCustomerSearch} placeholder="Search customer…" />
                {filteredCustomers.map(c => (
                  <CheckItem
                    key={c.id}
                    label={c.name}
                    checked={(local.customers || []).includes(c.id)}
                    onChange={() => toggle('customers', c.id)}
                  />
                ))}
                {filteredCustomers.length === 0 && (
                  <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>
                )}
              </>
            ) : (
              <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No data available</p>
            )}
          </FilterSection>

          {/* Sales Status */}
          <FilterSection title="Sales Status" count={local.statuses?.length || 0}>
            {statuses.length > 0 ? (
              statuses.map(s => (
                <CheckItem
                  key={s.id}
                  label={s.name}
                  checked={(local.statuses || []).includes(s.id)}
                  onChange={() => toggle('statuses', s.id)}
                />
              ))
            ) : (
              <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No statuses available</p>
            )}
          </FilterSection>
        </div>
      )}

      {/* Footer */}
      <div className="tw-px-3 tw-py-3 tw-border-t tw-border-gray-100 tw-flex-shrink-0" style={{ boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.05)' }}>
        <Button
          onClick={() => onApply(local)}
          className="tw-w-full tw-justify-center tw-py-2 tw-text-sm tw-font-semibold tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-rounded"
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

SalesFilterSidebar.propTypes = {
  filterOptions: PropTypes.object,
  loading:       PropTypes.bool,
  filters:       PropTypes.object,
  onApply:       PropTypes.func.isRequired,
  onReset:       PropTypes.func.isRequired,
  containerHeight: PropTypes.string
};
SalesFilterSidebar.defaultProps = { filterOptions: null, loading: false, filters: {}, containerHeight: 'max(320px, calc(100dvh - 110px))' };

export default SalesFilterSidebar;
